import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { getAllMaintenanceSchedule } from "../../../redux/MaintenanceScheduleSlice/MaintenanceScheduleSlice";
import MaintenanceSchedulesCard from "./MiniComponents/MaintenanceSchedulesCard";
import { getContractById } from "../../../redux/contractSlice/contractSlice";
import "./MaintenanceSchedules.css";

export default function MaintenanceSchedules() {
  const dispatch = useDispatch();
  const { allMaintenanceSchedule } = useSelector(
    (state) => state.maintenanceSchedule
  );
  const { detailsContract } = useSelector((state) => state.contract);
  const { ContractId } = useParams();

  // Local State
  const [allSchedulesData, setAllSchedulesData] = useState([]);
  const [displayedSchedules, setDisplayedSchedules] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  // جلب بيانات العقد إذا كان محدد
  useEffect(() => {
    if (ContractId) {
      dispatch(getContractById(ContractId));
    }
  }, [dispatch, ContractId]);

  // جلب كل البيانات عند التحميل (إذا لم يكن في Contract محدد)
  useEffect(() => {
    const fetchAllSchedules = async () => {
      if (!ContractId) {
        try {
          setLoading(true);

          // جلب أول صفحة
          const firstPage = await dispatch(
            getAllMaintenanceSchedule(1)
          ).unwrap();
          let allData = [...(firstPage.data || [])].filter(
            (schedule) => schedule.deleted_at == null
          );

          const totalPagesCount = firstPage.pagination
            ? Math.ceil(firstPage.pagination.total / firstPage.pagination.limit)
            : 1;

          setTotalPages(totalPagesCount);

          // جلب باقي الصفحات
          if (totalPagesCount > 1) {
            const promises = [];
            for (let page = 2; page <= totalPagesCount; page++) {
              promises.push(dispatch(getAllMaintenanceSchedule(page)).unwrap());
            }

            const otherPages = await Promise.all(promises);
            otherPages.forEach((pageData) => {
              if (pageData.data) {
                const filteredData = pageData.data.filter(
                  (schedule) => schedule.deleted_at == null
                );
                allData = [...allData, ...filteredData];
              }
            });
          }

          console.log(`✅ Loaded all ${allData.length} schedules`);
          setAllSchedulesData(allData);
          setDisplayedSchedules(allData);
        } catch (error) {
          console.error("❌ Error loading schedules:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchAllSchedules();
  }, [dispatch, ContractId]);

  // تحديث البيانات من Contract أو من كل الجداول
  useEffect(() => {
    if (ContractId) {
      const contractSchedules = detailsContract?.maintenanceSchedules || [];
      setAllSchedulesData(contractSchedules);
      setDisplayedSchedules(contractSchedules);
      setLoading(false);
    }
  }, [ContractId, detailsContract]);

  // البحث المحلي في الـ Frontend
  const searchInFrontend = useCallback((keyword, data) => {
    if (!keyword.trim()) {
      return data;
    }

    const searchTerm = keyword.toLowerCase().trim();

    return data.filter((schedule) => {
      const title = schedule.title?.toLowerCase() || "";
      const status = schedule.status?.toLowerCase() || "";
      const priority = schedule.priority?.toLowerCase() || "";
      const scheduleDate = schedule.schedule_date || "";
      const companyName =
        schedule.contractSchedule?.branch?.company?.name?.toLowerCase() || "";
      const branchName =
        schedule.contractSchedule?.branch?.name?.toLowerCase() || "";
      const contractNumber =
        schedule.contractSchedule?.contract_number?.toLowerCase() || "";
      const technicianName =
        schedule.assignedTechnician?.name?.toLowerCase() || "";
      const managerName =
        schedule.maintenanceManager?.name?.toLowerCase() || "";

      return (
        title.includes(searchTerm) ||
        status.includes(searchTerm) ||
        priority.includes(searchTerm) ||
        scheduleDate.includes(searchTerm) ||
        companyName.includes(searchTerm) ||
        branchName.includes(searchTerm) ||
        contractNumber.includes(searchTerm) ||
        technicianName.includes(searchTerm) ||
        managerName.includes(searchTerm)
      );
    });
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((keyword) => {
      console.log("🔍 Frontend Search for:", keyword);

      if (keyword.trim() !== "") {
        setIsSearching(true);
        const results = searchInFrontend(keyword, allSchedulesData);
        console.log(`✅ Found ${results.length} results`);
        setDisplayedSchedules(results);
      } else {
        setIsSearching(false);
        setDisplayedSchedules(allSchedulesData);
      }
    }, 300),
    [allSchedulesData, searchInFrontend]
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    setIsSearching(false);
    setDisplayedSchedules(allSchedulesData);
  };

  // Handle pagination (للعرض العادي بدون Contract)
  const handlePageChange = (page) => {
    if (!ContractId && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="main">
      <section className="maintenance-items">
        <div>
          <h1 className="text-white">
            {ContractId ? "جداول العقد" : "الجداول"}
          </h1>
        </div>
        <div className="container-fluid">
          <div className="row justify-content-between align-items-center mb-5 mt-3">
            {/* عدد الجداول */}
            <div className="col-md-3 d-flex justify-content-md-start justify-content-center mb-3">
              <div className="card py-2 px-3">
                <p className="mb-0 primary-100">
                  {isSearching ? (
                    <>
                      نتائج البحث:{" "}
                      <span className="primary-100">
                        {displayedSchedules.length}
                      </span>
                      <small
                        className="text-muted d-block"
                        style={{ fontSize: "0.8rem" }}
                      >
                        (من إجمالي {allSchedulesData.length})
                      </small>
                    </>
                  ) : (
                    <>
                      عدد الجداول:{" "}
                      <span className="primary-100">
                        {allSchedulesData?.length || 0}
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Search Box */}
            <div className="col-md-5 text-center mb-3">
              <div className="search-box-schedules position-relative">
                <input
                  type="text"
                  placeholder="ابحث بالعنوان، الشركة، الفرع، الحالة، رقم العقد..."
                  className="form-control search-input-schedules"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <i className="fas fa-search search-icon-schedules"></i>
                {searchQuery && (
                  <button
                    className="btn-clear-search-schedules"
                    onClick={clearSearch}
                    title="مسح البحث"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
              {isSearching && (
                <div className="search-info-schedules text-center mt-2">
                  <small className="text-success">
                    <i className="fas fa-check-circle me-1"></i>
                    وجد {displayedSchedules.length} نتيجة للبحث عن "
                    {searchQuery}"
                  </small>
                </div>
              )}
            </div>

            {/* زر إضافة جدول */}
            <div className="col-md-3 text-md-end text-center mb-3">
              <Link
                className="btn btn-primary text-white"
                to={"/AddMaintenanceSchedule"}
              >
                <i className="fas fa-plus me-2"></i>
                إضافة جدول
              </Link>
            </div>
          </div>

          {/* Loading Spinner */}
          {loading ? (
            <div className="loading-spinner-schedules">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
              </div>
              <p className="text-center mt-3 text-muted">
                جاري تحميل جميع الجداول...
              </p>
            </div>
          ) : (
            <>
              {/* Schedules Grid */}
              <div className="row">
                {displayedSchedules && displayedSchedules.length > 0 ? (
                  displayedSchedules.map((maintenanceSchedule) => (
                    <div
                      className="col-xl-4 col-lg-4 col-md-6 col-sm-6 col-12 mb-3"
                      key={maintenanceSchedule.id}
                    >
                      <MaintenanceSchedulesCard
                        maintenanceSchedule={maintenanceSchedule}
                      />
                    </div>
                  ))
                ) : (
                  <div className="col-12 text-center py-5">
                    <i className="fas fa-calendar-alt fa-3x text-muted mb-3"></i>
                    <p className="text-muted">
                      {searchQuery
                        ? `لا توجد نتائج للبحث عن "${searchQuery}"`
                        : "لا توجد جداول صيانة"}
                    </p>
                    {searchQuery && (
                      <button
                        className="btn btn-sm btn-primary mt-2"
                        onClick={clearSearch}
                      >
                        <i className="fas fa-times me-2"></i>
                        مسح البحث وعرض كل الجداول
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Pagination - فقط للعرض العادي بدون Contract وبدون بحث */}
              {!ContractId && !isSearching && totalPages > 1 && (
                <div className="row mt-4">
                  <div className="col-12 d-flex justify-content-center">
                    <nav>
                      <ul className="pagination">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            السابق
                          </button>
                        </li>
                        {[...Array(totalPages).keys()].map((page) => (
                          <li
                            key={page + 1}
                            className={`page-item ${
                              currentPage === page + 1 ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(page + 1)}
                            >
                              {page + 1}
                            </button>
                          </li>
                        ))}
                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            التالي
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              )}

              {/* Results Summary */}
              {displayedSchedules.length > 0 && (
                <div className="results-summary-schedules text-center mt-4">
                  <p className="text-muted">
                    عرض {displayedSchedules.length} جدول صيانة
                    {isSearching && ` من إجمالي ${allSchedulesData.length}`}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
